import {
  createContext,
  Dispatch,
  ReactElement,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { Difference } from '@modules-deprecated/bankReconciliation/types'

import { getSubjectReconciliationDifference, getTypeFromPayableSubject } from '../utils'
import { useMultiplePayableSubjects } from './multiplePayableSubjectsContext'
import { usePaymentModal } from './paymentModalContext'

interface ContextState {
  crossCurrencyPaymentAmount: number | undefined
  hasManualPaymentDifference: boolean
  manualPaymentAmount: number
  manualPaymentDifference: Difference
  setCrossCurrencyPaymentAmount: Dispatch<SetStateAction<number | undefined>>
  setManualPaymentAmount: Dispatch<SetStateAction<number>>
}

const ManualPaymentContext = createContext<ContextState | undefined>(undefined)

interface ManualPaymentContextProps {
  children?: ReactNode
}

export const ManualPaymentContextProvider = ({ children }: ManualPaymentContextProps): ReactElement => {
  const { selectedSubjectsAmountsSum, convertedSubjectsAmountsSum, isCreditNoteSelected, initialSubject } =
    useMultiplePayableSubjects()
  const { isCrossCurrency } = usePaymentModal()
  const [manualPaymentAmount, setManualPaymentAmount] = useState(0)
  // State to store the user input for cross currency payment amount in the case of partial or overpayment
  const [crossCurrencyPaymentAmount, setCrossCurrencyPaymentAmount] = useState(selectedSubjectsAmountsSum || undefined)

  const manualPaymentDifference = useMemo(() => {
    return getSubjectReconciliationDifference({
      isCreditNote: isCreditNoteSelected,
      reconciliationAmount: manualPaymentAmount,
      subjectsAmount: selectedSubjectsAmountsSum,
      subjectType: getTypeFromPayableSubject(initialSubject),
    })
  }, [initialSubject, isCreditNoteSelected, manualPaymentAmount, selectedSubjectsAmountsSum])

  const hasManualPaymentDifference = useMemo(() => {
    return !!manualPaymentDifference?.differenceAmount
  }, [manualPaymentDifference?.differenceAmount])

  useEffect(() => {
    if (isCrossCurrency) {
      setManualPaymentAmount(convertedSubjectsAmountsSum)
    } else if (selectedSubjectsAmountsSum) {
      setManualPaymentAmount(selectedSubjectsAmountsSum)
    }
  }, [convertedSubjectsAmountsSum, isCrossCurrency, selectedSubjectsAmountsSum])

  useEffect(() => {
    if (selectedSubjectsAmountsSum) {
      setCrossCurrencyPaymentAmount(selectedSubjectsAmountsSum)
    }
  }, [selectedSubjectsAmountsSum, setCrossCurrencyPaymentAmount])

  return (
    <ManualPaymentContext.Provider
      value={{
        crossCurrencyPaymentAmount,
        hasManualPaymentDifference,
        manualPaymentAmount,
        manualPaymentDifference,
        setCrossCurrencyPaymentAmount,
        setManualPaymentAmount,
      }}
    >
      {children}
    </ManualPaymentContext.Provider>
  )
}

export const useManualPayment = () => {
  const context = useContext(ManualPaymentContext)

  if (!context) {
    throw new Error('ManualPaymentContextProvider is missing in the module!')
  }

  return context
}
